import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Share2, Gift, CheckCircle2, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    getReferralCode,
    getReferralLink,
    getReferrals,
    getReferralEarnings,
    REFERRAL_REWARD_NGN,
    subscribeReferral,
} from "@/lib/referralStore";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();

const Referral = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [copied, setCopied] = useState(false);
    const [v, setV] = useState(0);

    useEffect(() => subscribeReferral(() => setV((x) => x + 1)), []);

    const code = useMemo(() => (user ? getReferralCode(user.id) : ""), [user]);
    const link = useMemo(() => (user ? getReferralLink(user.id) : ""), [user]);
    const referrals = useMemo(() => (user ? getReferrals(user.id) : []), [user, v]);
    const earnings = useMemo(() => (user ? getReferralEarnings(user.id) : 0), [user, v]);
    const rewardedCount = referrals.filter((r) => r.rewarded).length;

    const copy = async () => {
        try {
            await navigator.clipboard?.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {}
    };

    const share = async () => {
        const shareText = `Join me on Neyra — discover events & services near you. Use my code ${code} for bonus perks: ${link}`;
        if (navigator.share) {
            try { await navigator.share({ title: "Join Neyra", text: shareText, url: link }); }
            catch {}
        } else {
            copy();
        }
    };

    return (
        <div className="screen" data-testid="referral-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center gap-2">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="referral-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                    aria-label="Back"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">Invite & earn</h1>
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-4">
                {/* Hero / earnings */}
                <section
                    className="rounded-3xl p-5 text-primary-foreground"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.72) 100%)",
                    }}
                    data-testid="referral-hero"
                >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-white/80">
                        <Gift size={13} />
                        Total earnings
                    </div>
                    <div className="mt-2 font-display text-[32px] font-semibold leading-tight" data-testid="referral-earnings">
                        {fmt(earnings)}
                    </div>
                    <p className="mt-1 text-[12.5px] text-white/85">
                        Earn {fmt(REFERRAL_REWARD_NGN)} for every friend who completes their first transaction.
                    </p>
                </section>

                {/* Code + link */}
                <section className="p-4 rounded-2xl border border-border bg-card space-y-3" data-testid="referral-share-card">
                    <div>
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Your code</div>
                        <div className="mt-0.5 font-display text-[20px] font-semibold tracking-wide" data-testid="referral-code">
                            {code}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="referral-copy-btn"
                            onClick={copy}
                            className="flex-1 h-11 rounded-xl bg-secondary border border-border text-foreground inline-flex items-center justify-center gap-2 text-[13px] font-medium hover:bg-muted active:scale-[0.98]"
                        >
                            {copied ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Copy size={14} />}
                            {copied ? "Copied" : "Copy link"}
                        </button>
                        <button
                            data-testid="referral-invite-btn"
                            onClick={share}
                            className="flex-1 h-11 rounded-xl text-primary-foreground inline-flex items-center justify-center gap-2 text-[13px] font-medium active:scale-[0.98]"
                            style={{
                                background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                            }}
                        >
                            <Share2 size={14} />
                            Invite
                        </button>
                    </div>
                    <p className="text-[11.5px] text-muted-foreground break-all">{link}</p>
                </section>

                {/* Referrals list */}
                <section data-testid="referral-list-section">
                    <div className="flex items-center justify-between px-1 mb-2">
                        <h3 className="font-display text-[14px] font-semibold inline-flex items-center gap-1.5">
                            <Users size={14} className="text-primary" />
                            Your invites
                        </h3>
                        <span className="text-[11.5px] text-muted-foreground">
                            {rewardedCount}/{referrals.length} rewarded
                        </span>
                    </div>
                    {referrals.length === 0 ? (
                        <div className="py-8 text-center rounded-2xl border border-dashed border-border" data-testid="referral-empty">
                            <p className="text-[13px] font-medium">No invites yet</p>
                            <p className="mt-1 text-[11.5px] text-muted-foreground max-w-[30ch] mx-auto">
                                Share your link — you'll earn {fmt(REFERRAL_REWARD_NGN)} once a friend completes their first transaction.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-1.5">
                            {referrals.map((r) => (
                                <li
                                    key={r.userId}
                                    data-testid={`referral-row-${r.userId}`}
                                    className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card"
                                >
                                    <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                        <Users size={14} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] font-semibold truncate">
                                            {r.rewarded ? "Friend completed transaction" : "Friend joined"}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {new Date(r.at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <span
                                        className={[
                                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border",
                                            r.rewarded
                                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
                                                : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
                                        ].join(" ")}
                                    >
                                        {r.rewarded ? `+${fmt(r.amount || REFERRAL_REWARD_NGN)}` : "Pending"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Referral;
